import type { EditorView } from '@codemirror/view';
import clsx from 'clsx';
import { isArray } from 'lodash-es';
import {
  cloneElement,
  forwardRef,
  TouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { FormattedMessage } from 'react-intl';

import * as styles from './Suggestion.sty.js';
import type { FilterFieldSuggestionProps } from './Suggestion.types.js';
import { SuggestionDetails } from './SuggestionDetails.js';
import { SuggestionPin } from './SuggestionPin.js';
import { Chip } from '../../../content/chip/Chip.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useSlots } from '../../../core/hooks/useSlots.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../core/utils/can-receive-default-text-format.js';
import { getVirtualFocusStyles } from '../../../core/utils/getVirtualFocusStyles.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Grid } from '../../../layouts/grid/Grid.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import type { FilterFieldContextProps } from '../contexts/FilterFieldContext.types.js';
import {
  ANNOTATION_END_PASTING_MODE,
  pastingEffect,
  pastingMode,
  stringAnnotation,
} from '../editor-state-configs/editor-config.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { filterFieldMessages } from '../messages.js';
import { FilterFieldActions } from '../state/filter-field-state-reducer.js';
import type { FilterFieldNodeBase } from '../types/tree-nodes.js';
import type { FilterFieldValuePredicate } from '../types/validation.js';

/**
 * The highlight splits the text into a mark and a span, need to wrap it to keep it together
 * in the flex container.
 */
function WrappedSuggestionLabel({
  displayLabel,
}: Readonly<{
  displayLabel?: ReactNode | ReactNode[] | string;
}>): ReactNode {
  if (typeof displayLabel === 'string' || isArray(displayLabel)) {
    return <span className={styles.suggestionText}>{displayLabel}</span>;
  }

  return displayLabel;
}

/** @internal */
function getDescription(type: FilterFieldValuePredicate | undefined) {
  switch (type) {
    case 'Boolean':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.Boolean}
        />
      );
    case 'Duration':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.Durations}
        />
      );
    case 'Number':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.Numbers}
        />
      );
    case 'IPAddress':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.IPAddresses}
        />
      );
    case 'UID':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.UIDs}
        />
      );
    case 'SmartscapeId':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.SmartscapeIds}
        />
      );
    case 'Timestamp':
      return (
        <FormattedMessage
          {...filterFieldMessages.suggestions.supportedValues.Timestamps}
        />
      );
    case 'String':
    case 'Any':
    default:
      return undefined;
  }
}

function dispatchEndPastingModeEffect(editorView?: EditorView | null) {
  editorView?.dispatch({
    effects: [
      pastingEffect.of({
        pastingInProgress: false,
      }),
    ],
    // In order to re-run the decorations and re-generate errors and diagnostics,
    // we have to dispatch a new document.
    changes: {
      from: 0,
      to: editorView?.state.doc.length,
      insert: editorView?.state.doc,
    },
    selection: editorView.state.selection,
    annotations: stringAnnotation.of(ANNOTATION_END_PASTING_MODE),
  });
}

function handleSuggestionApply(
  editorView: EditorView | null,
  id: string | undefined,
  applySuggestion: FilterFieldContextProps['applySuggestion'],
  suggestionRef: HTMLElement | null,
  suggestionText: string,
  trailing: string | undefined,
  insertionStrategy:
    | 'replace-token'
    | 'replace-statement'
    | 'replace-all'
    | 'insert',
  insertionRange: FilterFieldNodeBase['range'] | undefined,
  cursorBeforeTrailing: boolean | undefined,
  escapeSuggestion?: boolean,
) {
  if (id === 'strato-filterfield-suggestion-pasted-content') {
    dispatchEndPastingModeEffect(editorView);
  } else {
    const pastingInProgress = editorView?.state.field(pastingMode, false);
    if (pastingInProgress?.pastingInProgress) {
      // stop pasting mode if a suggestion is applied while pasting mode is active
      dispatchEndPastingModeEffect(editorView);
    }
    applySuggestion(
      suggestionRef,
      suggestionText,
      trailing,
      insertionStrategy,
      cursorBeforeTrailing,
      insertionRange,
      escapeSuggestion,
    );
  }
}

/**
 * @public
 */
export const Suggestion = forwardRef<
  HTMLDivElement,
  FilterFieldSuggestionProps
>((props, forwardedRef) => {
  const {
    value,
    displayValue,
    details,
    trailing = ' ',
    cursorBeforeTrailing = false,
    insertionStrategy = 'replace-token',
    insertionRange,
    escapeSuggestion,
    valueType,
    className: consumerClassName,
    style: consumerStyle,
    icon,
    'data-testid': dataTestId,
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy,
    children,
    id,
    suggestionType,
    ...remainingProps
  } = props;

  const suggestionRef = useRef<HTMLElement | null>(null);
  const rootRef = useMergeRefs([forwardedRef, suggestionRef]);

  const { applySuggestion, dispatch, focusedListItem, editorView } =
    useFilterFieldContext();

  const valueRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const uniqueId = id ?? generatedId;

  const [slots, label] = useSlots(children, {
    details: SuggestionDetails,
    pin: SuggestionPin,
  });

  const childIsText = _canReceiveDefaultTextFormat(label);
  const displayValueIsText = _canReceiveDefaultTextFormat(displayValue);
  const isFocused = uniqueId === focusedListItem?.id ? true : undefined;

  const finalDetails = useMemo(() => {
    // User-provided <FilterField.SuggestionDetails> slot — merge in the
    // matchable marker class so CSS Custom Highlight can paint matches inside
    // the details text. The consumer's own className is preserved via clsx.
    if (slots.details) {
      return cloneElement(slots.details, {
        className: clsx(
          slots.details.props.className,
          styles.matchableSuggestionDetails,
        ),
      });
    }

    // Plain string `details` — wrap in SuggestionDetails and tag it matchable.
    // String details participate in filtering/sorting via suggestion-matching.ts,
    // so the highlight class belongs here too (keeps the two in sync).
    if (typeof details === 'string') {
      return (
        <SuggestionDetails className={styles.matchableSuggestionDetails}>
          {details}
        </SuggestionDetails>
      );
    }

    // ReactNode `details` (e.g. operator <FormattedMessage>) — deliberately
    // NOT marked matchable: non-string details can't be substring-matched
    // against a typed term, so highlighting them would be misleading.
    if (details) {
      return <SuggestionDetails>{details}</SuggestionDetails>;
    }

    // No explicit details: fall back to a generated "Supported values: ..."
    // hint for typed value placeholders. Display-only, not a match target.
    if (suggestionType !== 'validatorMap') {
      const description = getDescription(valueType);

      if (description) {
        return <SuggestionDetails>{description}</SuggestionDetails>;
      }
    }

    return null;
  }, [details, slots.details, suggestionType, valueType]);

  const [showTooltip, setShowTooltip] = useState(false);

  if (value === undefined && !childIsText && !displayValueIsText) {
    // eslint-disable-next-line no-console
    console.warn(
      'Suggestion value: There is no value provided for a suggestion and the children are not text-only. The applied result may be unexpected.',
    );
  }

  let suggestionText = '';
  if (value) {
    suggestionText = value;
  } else if (childIsText) {
    suggestionText = label.join('');
  } else if (displayValueIsText) {
    suggestionText = displayValue as string;
  }

  if (typeof suggestionText === 'string' && !trailing) {
    suggestionText = suggestionText.trim();
  }

  useEffect(() => {
    if (typeof suggestionText === 'string') {
      dispatch({
        type: FilterFieldActions.ADDSUGGESTION,
        payload: {
          id: uniqueId,
          applySuggestion: () => {
            handleSuggestionApply(
              editorView,
              uniqueId,
              applySuggestion,
              suggestionRef.current,
              suggestionText,
              trailing,
              insertionStrategy,
              insertionRange,
              cursorBeforeTrailing,
              escapeSuggestion,
            );
          },
        },
      });
    }

    return () =>
      dispatch({
        type: FilterFieldActions.REMOVESUGGESTION,
        payload: { id: uniqueId },
      });
    // Only run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (typeof suggestionText !== 'string') {
    return null;
  }

  const displayLabel = label.length > 0 ? label : displayValue ?? value;

  // Placeholder for mostly empty / hint suggestions. Not a selectable option
  if (
    !value &&
    trailing === '' &&
    id !== 'strato-filterfield-suggestion-pasted-content'
  ) {
    return (
      <Flex
        {...remainingProps}
        role="presentation"
        key={value}
        justifyContent="space-between"
        className={
          id === 'strato-filterfield-suggestion-jsonpath-hint'
            ? styles.suggestionsJSONPathHint
            : styles.suggestionsEmptyState
        }
        ref={rootRef}
      >
        <span>{displayLabel}</span>
        {finalDetails}
      </Flex>
    );
  }

  return (
    <Flex
      {...remainingProps}
      role={slots.pin && 'group'}
      gap={0}
      data-testid={dataTestId}
      className={clsx(styles.suggestion, consumerClassName)}
      onTouchEnd={(e: TouchEvent) => {
        // Defaults for on-touch-end events needs to be prevented, as otherwise on touch devices the suggestion window
        // gets closed when clicking on suggestions. Note: This can be emulated via dev tools but only happens
        // on deployed apps (not happening in local dev mode)
        e.preventDefault();
      }}
      style={consumerStyle}
      ref={rootRef}
    >
      <Tooltip
        text={displayLabel}
        placement="left"
        open={showTooltip}
        className={styles.tooltipWrapper}
        onOpenChange={(isOpen) => {
          const valueNodeChild = valueRef.current?.children[0];

          const hasOverFlow =
            (valueNodeChild?.scrollWidth ?? 0) >
            (valueNodeChild?.clientWidth ?? 0);

          setShowTooltip(isOpen && hasOverFlow);
        }}
      >
        <Grid
          id={uniqueId}
          role="option"
          aria-selected={isFocused}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-strato-filter-field-virtual-focus-id={uniqueId}
          data-strato-filter-field-virtual-focus-type="suggestion"
          columnGap={8}
          rowGap={0}
          className={clsx(
            styles.suggestionContent,
            getVirtualFocusStyles(!!isFocused).className,
            id === 'strato-filterfield-suggestion-pasted-content'
              ? styles.pastingSuggestion
              : {},
          )}
          paddingLeft={icon ? 8 : 12}
          // NOTE: There's an open issue in @testing-library/user-event where `onClick` is not triggered in tests when used
          // with `event.preventDefault`. Pointer down also works and enables easier testing of the suggestions.
          onPointerDown={() => {
            handleSuggestionApply(
              editorView,
              id,
              applySuggestion,
              suggestionRef.current,
              suggestionText,
              trailing,
              insertionStrategy,
              insertionRange,
              cursorBeforeTrailing,
              escapeSuggestion,
            );
          }}
          data-dt-name="Suggestion"
        >
          <Flex gap={8} minWidth={0}>
            {icon && (
              <Chip size="condensed" className={styles.icon}>
                <Chip.Prefix>{icon}</Chip.Prefix>
              </Chip>
            )}
            <Flex
              className={clsx(styles.value, {
                [`${styles.truncateWithinMark}`]:
                  id === 'strato-filterfield-suggestion-use-as-value' ||
                  id === 'strato-filterfield-suggestion-use-as-key' ||
                  id === 'strato-filterfield-suggestion-pasted-content' ||
                  id === 'strato-filterfield-suggestion-search',
              })}
              ref={valueRef}
            >
              <WrappedSuggestionLabel displayLabel={displayLabel} />
            </Flex>
          </Flex>
          {finalDetails}
        </Grid>
      </Tooltip>
      {slots.pin}
    </Flex>
  );
});

Object.assign(Suggestion, { displayValue: 'FilterField.Suggestion' });
